import {
  FormHelperText,
  Grid,
  Button,
  Typography,
  ImageListItem,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { CloudUpload } from "@material-ui/icons";
import clsx from "clsx";
import { Fragment, useCallback, useContext, useEffect, useState } from "react";
import Dropzone from "react-dropzone";
import LayoutContext from "../../context/layout/LayoutContext";
import CloudinaryContext from "../../context/cloudinary/CloudinaryContext";

const useStyles = makeStyles((theme) => ({
  section: {
    marginBottom: theme.spacing(2),
  },
  paper_00: {
    padding: theme.spacing(1, 2, 1, 2),
    color: theme.palette.text.secondary,
  },
  paper: {
    padding: theme.spacing(1, 2, 2, 2),
    color: theme.palette.text.secondary,
  },
  divider: {
    marginTop: "1rem",
    marginBottom: "0.5rem",
  },
  skeleton: {
    borderRadius: "5px",
  },
  dropzone: {
    minHeight: 150,
    width: "98%",
    margin: "0.5rem auto",
    border: "1px dashed lightgrey",
    alignItems: "center",
    textAlign: "center",
    color: "darkgrey",
  },
  dropzoneHover: {
    "&:hover": {
      cursor: "pointer",
      color: "grey",
      backgroundColor: "rgba(220, 220, 220, 0.2)",
      borderWidth: 3,
    },
  },
  dropzoneError: {
    borderWidth: 2,
    borderColor: theme.palette.error.main,
  },
  image: {
    objectFit: "contain",
  },
  imageContainer: {
    display: "flex",
    flexWrap: "wrap",
  },
  button: {
    marginRight: "8px",
    marginTop: "16px",
  },
  deleteButton: {
    height: "36px",
    width: "36px",
    backgroundColor: "#f8d7da",
    color: "#721c24",
    borderRadius: "9999px",
    marginTop: "6px",
    marginRight: "6px",
    border: "none"
  },
  list: {
    textAlign: "center",
    margin: 5,
  },
}));

const Avatar = () => {
  const classes = useStyles();

  const { setAlert } = useContext(LayoutContext);
  const { uploadImage, images, browseImage, deleteAvatar } =
    useContext(CloudinaryContext);
  const [avatar, setAvatar] = useState([]);
  const [imageSize, setImageSize] = useState(0);
  const [allowUpload, setAllowUpload] = useState(true);

  const onDrop = useCallback(
    (acceptedFiles, id) => {
      const files = acceptedFiles;
      const images = [];

      if (files.length > 0) {
        files.forEach((file) => {
          setImageSize(imageSize + file.size);
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onloadend = () => {
            // Preview Image
            images.push(reader.result);
            setAvatar([...avatar, ...images]);
          };
        });
      }
    },
    [avatar]
  );

  useEffect(() => {
    browseImage(false);
  }, []);

  useEffect(() => {
    if (imageSize > 5200000) {
      setAllowUpload(false);
      setAlert(
        "Maximum total 5mb of file sizes are allowed in one upload. Please upload the avatar in batches if you need to upload a lot of them",
        "error"
      );
    }
  }, [imageSize]);

  const onDiscard = () => {
    setAvatar([]);
    setImageSize(0);
    setAllowUpload(true);
  };

  const onSave = () => {
    uploadImage(avatar);
    setTimeout(() => {
      onDiscard();
    }, 5000);
  };

  const onDelete = (public_id) => {
    deleteAvatar(public_id);
  };

  const onDropRejected = useCallback(() => {
    setAlert("Image file smaller than 2mb only.", "error");
  }, []);

  const dropzone = (label, id, urls) => {
    return (
      <Dropzone
        accept="image/*"
        multiple={true}
        onDrop={(e) => onDrop(e, id)}
        onDropRejected={() => onDropRejected()}
        maxSize={2000000}
        disabled={!allowUpload}
      >
        {({ getRootProps, getInputProps }) => (
          <Grid item xs={12} className={classes.inputBox}>
            <Grid
              id={id}
              item
              container
              className={clsx({
                [classes.dropzone]: true,
                [classes.dropzoneHover]: true,
                [classes.dropzoneError]: avatar && !urls,
              })}
              {...getRootProps()}
            >
              <input id={id} {...getInputProps()} />
              {urls.length > 0 ? (
                <div>
                  {urls.map((url) => (
                    <img
                      height="110"
                      src={url}
                      className={classes.image}
                      alt=""
                    />
                  ))}
                </div>
              ) : (
                <span style={{ marginLeft: "40px" }}>
                  <CloudUpload />
                  <Typography variant="body2">
                    Drag and drop file here or click to select file
                  </Typography>
                </span>
              )}
            </Grid>
          </Grid>
        )}
      </Dropzone>
    );
  };

  return (
    <Fragment>
      <Grid container spacing={1} className={classes.section}>
        <Grid container spacing={0} justifyContent="flex-start">
          <Grid item xs={12}>
            <Typography variant="h4"> Avatar </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Fragment>
        <Grid item xs={12}>
          <FormHelperText margin="dense">
            * Drag and drop images here to upload
          </FormHelperText>
        </Grid>
        {dropzone("Store logo", "logo", avatar)}
      </Fragment>

      <Grid item xs={12} container justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={onDiscard}
          disabled={avatar.length < 1}
        >
          Remove all images
        </Button>

        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={onSave}
          disabled={avatar.length < 1}
        >
          Upload Avatar
        </Button>
      </Grid>

      {images && (
        <div className={classes.imageContainer} style={{ marginTop: "48px" }}>
          {images.map((image, index) => (
            <div
              className={clsx({
                [classes.list]: true,
              })}
              key={image.asset_id}
              style={{
                width: "30%",
                display: "flex",
                justifyContent: "space-between",
                filter:
                  "drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))",
                backgroundColor: "white",
              }}
            >
              <img
                height="130"
                width="130"
                src={image.secure_url}
                className={classes.image}
                alt=""
              />
              <button
                className={classes.deleteButton}
                onClick={() => onDelete(image.public_id)}
                title="Delete this avatar"
              >
                X
              </button>
            </div>
          ))}
        </div>
      )}
    </Fragment>
  );
};

export default Avatar;
